import { getRouteApi } from '@tanstack/react-router';
import { Label } from 'cpool-components/general/Label';
import { TextInput } from 'cpool-components/input/TextInput';
import { LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormField';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type { SchemaLivingSituationForm } from '~/features/leadDetails/schemas/living.schema.ts';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

type Props = {
    antragsteller: 1 | 2;
    addressKind: 'anschrift' | 'vorherigeAnschrift';
};

const StreetAndHouseNr: React.FC<Props> = ({ addressKind, antragsteller }) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { control } = useFormContext<SchemaLivingSituationForm>();
    const { t } = useTranslation();
    const isDisabled = !canExecuteApplicantCommand({
        applicant: antragsteller,
        commandName:
            addressKind === 'vorherigeAnschrift'
                ? LOAN_REQUEST_COMMANDS.changeApplicantPreviousAddress
                : LOAN_REQUEST_COMMANDS.changeApplicantAddress,
        permissions,
    });

    return (
        <div className="flex items-start gap-2.5">
            <Label className="h-8 w-[175px] shrink-0 justify-start">
                {t('ADDRESS.STREET')}
                {', '}
                {t('ADDRESS.HOUSE_NUMBER')}
            </Label>
            <div className="flex w-full min-w-0 items-start gap-2.5">
                <FormField<SchemaLivingSituationForm, string | null>
                    control={control}
                    name={`antragsteller${antragsteller}.wohnsituation.${addressKind}.strasse`}
                >
                    {({ error, onBlur, onChange, value }) => (
                        <TextInput
                            id={`antragsteller${antragsteller}.wohnsituation.${addressKind}.strasse`}
                            className="min-w-0 flex-1"
                            value={value ?? ''}
                            onBlur={onBlur}
                            onChange={(event) => onChange(event.target.value)}
                            disabled={isDisabled}
                            error={error}
                        />
                    )}
                </FormField>
                <FormField<SchemaLivingSituationForm, string | null>
                    control={control}
                    name={`antragsteller${antragsteller}.wohnsituation.${addressKind}.hausnummer`}
                >
                    {({ error, onBlur, onChange, value }) => (
                        <TextInput
                            id={`antragsteller${antragsteller}.wohnsituation.${addressKind}.hausnummer`}
                            className="w-[80px] shrink-0"
                            value={value ?? ''}
                            onBlur={onBlur}
                            onChange={(event) => onChange(event.target.value)}
                            disabled={isDisabled}
                            error={error}
                        />
                    )}
                </FormField>
            </div>
        </div>
    );
};

export default StreetAndHouseNr;
